import { ChangeDetectorRef, Component, NgZone, ViewChild, effect, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, take } from 'rxjs';
import { MoovService } from '../../../../services/MoovService';
import { FileItem } from '../../../../models/fileItem';
import { ConfirmationDialogComponent } from '../../confirmation-dialog.component';
import { AuthenticationService } from '../../../../services/AuthenticationService';
import { base64ToBlob } from '../../../../helpers/blob.utils';

@Component({ standalone:false,
  selector: 'app-show-file',
  templateUrl: 'show-file.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['show-file.component.scss']
})
export class ShowFileComponent {
  @ViewChild('fileOutput') fileOutput:any; 
  readonly fileItem = input.required<FileItem>();
  readonly dataChanged = output<any>();

  public fileItemObject?:FileItem;
  public downloadButtonText:string = "Download";
  public saveButtonText:string = "Save";
  public restoreButtonText:string = "Restore";
  public busy:boolean = false;

  constructor(private dialog: MatDialog, private moovService:MoovService, private authService:AuthenticationService, public snackBar: MatSnackBar,private ngZone: NgZone, private cd:ChangeDetectorRef) {
   effect(() => {
      const item = this.fileItem();
      if (!item) return;
      
      if (item.blob) {
        item.blob.text().then(content => {
          item.fileContent = content;
          this.ngZone.run(() => {
            this.fileItemObject = item;
            this.cd.markForCheck();
          });
        });
      } else {
        item.fileContent = 'no content';
        this.fileItemObject = item;
      }
    });
  }

  onContentUpdated(){
    this.fileItemObject!.fileContentDirty=true;
  }

  onDownloadClick(): void {
    const downloadAncher = document.createElement("a");
    downloadAncher.style.display = "none";
    const fileURL = URL.createObjectURL(this.fileItemObject!.blob);
    downloadAncher.href = fileURL;
    downloadAncher.download = this.fileItemObject!.fileNameWithExtension;
    downloadAncher.click();
  }

  onSaveClick(): void {
    if(this.fileItemObject!.fileContentDirty){
      this.confirmDialog("update", "Save Changes?", "Are you sure you want save the changes you've made to the data?", "Yes")
    }
  }

  onRestoreClick(): void {
    if(this.fileItemObject!.canRestore){
      this.confirmDialog("restore", "Remove all saved changes?", "Are you sure you want to remove all saved changes and restore the data back to its original state prior to any edits?", "Yes")
    }
  }

  private confirmDialog(action:string, title:string, message:string, okButtonText:string){
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose=true;
    dialogConfig.autoFocus=true;
    dialogConfig.data={title: title, message: message, buttonText: {ok: okButtonText, cancel: "no"}};
    this.dialog.open(ConfirmationDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(value=>{
      if(value){
        if(action=="update"){
          this.updateFile();
        }
        else if (action=="restore"){
          this.restoreFile();
        }
      }
    });
  }

  private async updateFile(): Promise<void> {
    this.busy = true;
    this.cd.detectChanges();

    const updatedBlob = new Blob([this.fileOutput.nativeElement.innerText], { type: 'text/plain' });
    const updatedFile = new File([updatedBlob], this.fileItemObject!.filePath!, { type: 'text/plain' });

    try {
      const r = await firstValueFrom(this.moovService.updateFile(updatedFile, this.fileItemObject!.moovFileId!, this.fileItemObject!.moovStepInstance!.id));

      if (r.success) {
        const fr = await firstValueFrom(this.moovService.getMoovFile(r.longCode, this.authService.customerContext?.id));
        const blob = await base64ToBlob(fr.base64File, 'text/plain');

        const msi = this.fileItemObject!.moovStepInstance!;

        const updatedFileItem = new FileItem(blob, msi.outputFile.displayName, msi.outputFile.filepath, '', this.fileItemObject!.canEdit, msi.outputFile.id, msi, undefined, undefined, msi.outputFile.added);
        await updatedFileItem.updateFileContent();

        this.ngZone.run(() => {
          msi.outputFile.id = r.longCode;
          msi.outputFile.filepath = fr.reference;
          this.fileItemObject = updatedFileItem;
          this.busy = false;
          this.cd.markForCheck();
          this.snackBar.open('File has been updated!', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
          this.dataChanged.emit(undefined);
        });
      } else {
        this.snackBar.open('Error updating file: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 0 });
        this.busy = false;
        this.cd.markForCheck();
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      this.snackBar.open('Unexpected error updating file: ' + message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 0 });
      this.busy = false;
      this.cd.markForCheck();
    }
  }

  private async restoreFile(): Promise<void> {
    this.busy = true;
    this.cd.markForCheck();

    try {
      const r = await firstValueFrom(this.moovService.restoreFile(this.fileItemObject!.moovFileId!, this.fileItemObject!.moovStepInstance!.id));

      if (r.success) {
        const fr = await firstValueFrom(this.moovService.getMoovFile(this.fileItemObject!.moovStepInstance!.inputFile.id, this.authService.customerContext?.id));
        const blob = await base64ToBlob(fr.base64File, 'text/plain');

        const msi = this.fileItemObject!.moovStepInstance!;

        const restoredFileItem = new FileItem(blob, msi.outputFile.displayName, msi.outputFile.filepath, '', this.fileItemObject!.canEdit, msi.outputFile.id, msi, undefined, undefined, msi.outputFile.added);
        await restoredFileItem.updateFileContent();

        this.ngZone.run(() => {
          msi.outputFile.id = msi.inputFile.id;
          msi.outputFile.filepath = msi.inputFile.filepath;
          this.fileItemObject = restoredFileItem;
          this.busy = false;
          this.cd.markForCheck();
          this.snackBar.open('File has been restored!', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
          this.dataChanged.emit(undefined);
        });
      } else {
        this.snackBar.open('Error restoring file: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 0 });
        this.busy = false;
        this.cd.markForCheck();
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      this.snackBar.open('Unexpected error restoring file: ' + message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 0 });
      this.busy = false;
      this.cd.markForCheck();
    }
  }

}